import * as Sentry from "@sentry/react";
import { logger } from "../../logger";
import { WalletConnectTimeoutError } from "./types";

/**
 * Bounded waits for the wallet connect flow.
 *
 * Three different windows, because they answer three different questions. Two of
 * them exist only so that a wallet SDK behaving badly can never wedge the UI —
 * see the `connect()` contract in `types.ts` for what that failure looks like.
 */

/**
 * How long to wait after an SDK's modal closes before treating a still-pending
 * connect as a cancellation.
 *
 * Ported verbatim from the Para adapter, where it was validated in live QA
 * against the exact race it guards: the modal closes on SUCCESS too, and the
 * provider/account only materialise once the SDK resolves an account — strictly
 * after that close. Rejecting the instant the modal closed would reject
 * successful connections. So the cancel is deferred and then re-checked.
 *
 * It is shared rather than duplicated because two magic numbers for one
 * phenomenon is how they drift apart.
 */
export const CONNECT_SETTLE_GRACE_MS = 1500;

/**
 * The adapter-level hard cap: an SDK that never answers at all.
 *
 * 120s is deliberate and is NOT a UI-responsiveness number. A legitimate connect
 * can contain a human typing a 6-digit email OTP, an OAuth popup round trip, a
 * passkey/biometric prompt, or a WalletConnect QR scanned on a second device.
 * A 30s cap would abort real users mid-OTP — a worse bug than the one being
 * fixed. This sits well above the p99 of those flows while still being bounded.
 *
 * The ordinary cancel never reaches this timer; the grace window above catches
 * it in 1.5s. This is purely the escape hatch for the pathological case — which
 * is exactly the reported incident, where the modal never opened at all because
 * `login()` no-opped, so there was no close edge to detect.
 */
export const CONNECT_HARD_TIMEOUT_MS = 120_000;

/**
 * The net around `useWallet.activate()`.
 *
 * Longer than the adapter cap on purpose. When both are armed the adapter's more
 * specific, better-instrumented rejection wins the race; this only actually
 * fires for an adapter with no pending-connect helper of its own — today the
 * inert web3-react controller, tomorrow whatever someone adds without reading
 * the contract. That is the point: this layer is not opt-in.
 */
export const ACTIVATE_NET_TIMEOUT_MS = 150_000;

/**
 * Reject `promise` if it has not settled within `ms`.
 *
 * A timeout here means a wallet SDK failed to answer, which users cannot see and
 * monitoring has never been able to see either — a non-settling promise emits no
 * rejection, so there is no `unhandledrejection`, no console error, and no
 * Sentry event. Reporting it is most of the value of this function.
 *
 * The timer is always cleared, including on the success path, so a resolved
 * connect leaves nothing pending behind it.
 */
export const withConnectTimeout = async <T,>(
  promise: Promise<T>,
  ms: number,
  context: () => Record<string, unknown> = () => ({})
): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      const error = new WalletConnectTimeoutError(
        `Wallet connection did not complete within ${ms}ms`
      );
      let extra: Record<string, unknown> = {};
      try {
        extra = context();
      } catch {
        // Diagnostics must never be the reason a timeout fails to report.
      }
      logger.warn("[wallet] connect timed out", { ms, ...extra });
      Sentry.captureException(error, {
        tags: { area: "wallet-connect" },
        extra: { timeoutMs: ms, ...extra },
      });
      reject(error);
    }, ms);
  });

  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
};
